/**
 * Shopping Cart Module
 *
 * Combined cart functionality including:
 * - Core cart API (localStorage-based)
 * - UI handlers (add to cart buttons, badge updates)
 * - Cart page initialization (data-init pattern)
 */

import { register as toolbarRegister, SLOT as TOOLBAR_SLOT } from '../floating-toolbar.js'

// =============================================================================
// CORE CART MODULE (localStorage-based)
// =============================================================================

const Cart = (function() {
  const STORAGE_KEY = 'cart';
  const VERSION = 2;

  // Check if localStorage is available
  function isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Stable identity for a cart line: same productId + same customizations -> same line.
  function computeLineKey(productId, customizations) {
    if (!customizations || customizations.length === 0) {
      return productId;
    }
    return productId + '#' + JSON.stringify(customizations);
  }

  // Get cart data from localStorage
  function getCartData() {
    if (!isLocalStorageAvailable()) {
      throw new Error('localStorage is not available. Please enable cookies/storage in your browser.');
    }

    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return { items: [], updatedAt: Date.now(), version: VERSION };
      }

      const cart = JSON.parse(data);

      // Validate schema
      if (!cart.items || !Array.isArray(cart.items)) {
        console.warn('Invalid cart schema, resetting cart');
        return { items: [], updatedAt: Date.now(), version: VERSION };
      }

      // v1 items have no customizations / lineKey. Backfill both.
      if (cart.version !== VERSION) {
        cart.items.forEach(item => {
          if (!Array.isArray(item.customizations)) item.customizations = [];
          if (!item.lineKey) item.lineKey = computeLineKey(item.productId, item.customizations);
        });
        cart.version = VERSION;
      }

      return cart;
    } catch (e) {
      console.error('Failed to parse cart data:', e);
      return { items: [], updatedAt: Date.now(), version: VERSION };
    }
  }

  // Save cart data to localStorage
  function saveCartData(cart) {
    if (!isLocalStorageAvailable()) {
      throw new Error('localStorage is not available');
    }

    cart.updatedAt = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));

    // Trigger custom event for UI updates
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
  }

  // Public API
  return {
    isAvailable() {
      return isLocalStorageAvailable();
    },

    getItems() {
      const cart = getCartData();
      return cart.items;
    },

    addItem(item, quantity = 1) {
      const cart = getCartData();

      // Validate item
      if (!item.productId || !item.name || !item.price) {
        throw new Error('Invalid item: missing required fields (productId, name, price)');
      }

      if (quantity < 1) {
        throw new Error('Quantity must be at least 1');
      }

      const maxQuantity = parseInt(item.maxQuantity, 10) || 99;
      const customizations = Array.isArray(item.customizations) ? item.customizations : [];
      const lineKey = computeLineKey(item.productId, customizations);

      // Same productId + same customizations -> same line. Different customizations -> new line.
      const existingItem = cart.items.find(i => i.lineKey === lineKey);

      if (existingItem) {
        const newQty = existingItem.quantity + quantity;
        if (newQty > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        existingItem.quantity = newQty;
        // Update maxQuantity in case it changed in the catalog
        existingItem.maxQuantity = maxQuantity;
      } else {
        if (quantity > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        cart.items.push({
          lineKey: lineKey,
          productId: item.productId,
          name: item.name,
          price: parseInt(item.price, 10),
          currency: item.currency || 'usd',
          quantity: quantity,
          maxQuantity: maxQuantity,
          imageUrl: item.imageUrl || '',
          esignTemplateId: item.esignTemplateId || '',
          customizations: customizations
        });
      }

      saveCartData(cart);
      return cart.items;
    },

    updateQuantity(lineKey, quantity) {
      const cart = getCartData();
      const item = cart.items.find(i => i.lineKey === lineKey);

      if (!item) {
        throw new Error('Item not found in cart');
      }

      if (quantity <= 0) {
        cart.items = cart.items.filter(i => i.lineKey !== lineKey);
      } else {
        const maxQuantity = item.maxQuantity || 99;
        if (quantity > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        item.quantity = quantity;
      }

      saveCartData(cart);
      return cart.items;
    },

    removeItem(lineKey) {
      const cart = getCartData();
      cart.items = cart.items.filter(i => i.lineKey !== lineKey);
      saveCartData(cart);
      return cart.items;
    },

    clear() {
      const cart = { items: [], updatedAt: Date.now(), version: VERSION };
      saveCartData(cart);
      return cart.items;
    },

    getTotals() {
      const items = this.getItems();

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      return {
        itemCount,
        subtotal,
        currency: items.length > 0 ? items[0].currency : 'usd'
      };
    },

    getCheckoutPayload() {
      const items = this.getItems();
      const payload = {
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          customizations: item.customizations || []
        }))
      };
      const discount = this.getDiscount();
      if (discount && discount.code) {
        payload.discountCode = discount.code;
      }
      return payload;
    },

    // Persisted in sessionStorage so the code survives the eSign hop
    // and Stripe Checkout redirects, mirroring `checkout_session_id`.
    getDiscount() {
      try {
        const raw = sessionStorage.getItem('discount_code');
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    },

    setDiscount(discount) {
      try {
        sessionStorage.setItem('discount_code', JSON.stringify(discount));
      } catch (e) {
        // sessionStorage unavailable — fail open and let the user re-enter.
      }
    },

    clearDiscount() {
      try {
        sessionStorage.removeItem('discount_code');
      } catch (e) {
        // ignore
      }
    }
  };
})();

// Create floating cart icon
function createFloatingCartIcon() {
  // Check if cart icon already exists
  if (document.getElementById('floating-cart-icon')) return;

  const cartIcon = document.createElement('a');
  cartIcon.id = 'floating-cart-icon';
  cartIcon.href = '/app/shop/cart';

  // Determine initial visibility
  const totals = Cart.getTotals();
  const shouldShow = totals.itemCount > 0 || document.body.hasAttribute('data-cart-show');

  // Apply explicit inline styles with CSS resets for consistency
  cartIcon.style.cssText = `
    all: unset;
    position: fixed;
    bottom: 20px;
    width: ${TOOLBAR_SLOT || 50}px;
    height: ${TOOLBAR_SLOT || 50}px;
    border-radius: 50%;
    display: ${shouldShow ? "flex" : "none"};
    align-items: center;
    justify-content: center;
    background-color: #0d6efd;
    color: white;
    text-decoration: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
    box-sizing: border-box;
  `;

  // Create icon element with reset styles
  const icon = document.createElement('span');
  icon.style.cssText = `
    all: unset;
    width: 24px;
    height: 24px;
    display: block;
  `;
  icon.setAttribute('aria-hidden', 'true');

  // Use inline SVG for cart icon (no external dependencies)
  icon.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="currentColor" style="display: block; width: 100%; height: 100%;">
      <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l.84 4.479 9.144-.459L13.89 4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
    </svg>
  `;

  // Create badge with reset styles
  const badge = document.createElement('span');
  badge.id = 'floating-cart-badge';
  badge.style.cssText = `
    all: unset;
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 20px;
    height: 20px;
    padding: 2px 6px;
    border-radius: 10px;
    background-color: #dc3545;
    color: white;
    font-size: 11px;
    font-weight: bold;
    line-height: 16px;
    text-align: center;
    display: none;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  `;
  badge.textContent = '0';

  cartIcon.appendChild(icon);
  cartIcon.appendChild(badge);

  // Hover effect
  cartIcon.addEventListener('mouseenter', () => {
    cartIcon.style.transform = 'scale(1.1)';
    cartIcon.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
  });
  cartIcon.addEventListener('mouseleave', () => {
    cartIcon.style.transform = 'scale(1)';
    cartIcon.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
  });

  document.body.appendChild(cartIcon);

  // Register with floating toolbar (lower order = further right)
  toolbarRegister(cartIcon, 10);

  // Add animation styles with namespaced class
  const style = document.createElement('style');
  style.textContent = `
    @keyframes shop-cart-badge-bounce {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.2); }
    }
    #floating-cart-badge.shop-cart-badge-bounce {
      animation: shop-cart-badge-bounce 0.3s ease-in-out;
    }
  `;
  document.head.appendChild(style);
}

// Initialize cart and show error if localStorage unavailable
document.addEventListener('DOMContentLoaded', function() {
  // Create floating cart icon
  createFloatingCartIcon();

  if (!Cart.isAvailable()) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-danger alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
    errorDiv.style.zIndex = '9999';
    errorDiv.innerHTML = `
      <i class="bi bi-exclamation-triangle me-2"></i>
      <strong>Shopping cart unavailable:</strong> Please enable cookies/storage in your browser settings.
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.appendChild(errorDiv);

    document.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.disabled = true;
      button.title = 'Cart unavailable (localStorage required)';
    });

    console.error('Shopping cart unavailable: localStorage is not supported or disabled');
  }
});

// =============================================================================
// UI HANDLERS (badge updates, add to cart buttons)
// =============================================================================

(function() {
  'use strict';

  // Update cart badge (both header and floating icon)
  function updateCartBadge() {
    if (!Cart.isAvailable()) return;

    const totals = Cart.getTotals();
    const shouldShow = totals.itemCount > 0 || document.body.hasAttribute('data-cart-show');

    // Update floating cart icon visibility
    const cartIcon = document.getElementById('floating-cart-icon');
    if (cartIcon) {
      cartIcon.style.display = shouldShow ? 'flex' : 'none';
    }

    // Update floating cart badge
    const floatingBadge = document.getElementById('floating-cart-badge');
    if (floatingBadge) {
      if (totals.itemCount > 0) {
        floatingBadge.textContent = totals.itemCount;
        floatingBadge.style.display = 'block';

        // Animate badge
        floatingBadge.classList.remove('shop-cart-badge-bounce');
        void floatingBadge.offsetWidth; // Trigger reflow
        floatingBadge.classList.add('shop-cart-badge-bounce');
      } else {
        floatingBadge.style.display = 'none';
      }
    }

    // Update header cart badge (if it exists)
    const badge = document.getElementById('cart-badge');
    if (badge) {
      if (totals.itemCount > 0) {
        badge.textContent = totals.itemCount;
        badge.style.display = 'inline-block';

        // Animate badge
        badge.classList.remove('cart-badge-bounce');
        void badge.offsetWidth; // Trigger reflow
        badge.classList.add('cart-badge-bounce');
      } else {
        badge.style.display = 'none';
      }
    }
  }

  // Show toast notification
  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
    toast.style.cssText = 'bottom: 10px; right: 100px; z-index: 9999; min-width: 300px;';
    toast.innerHTML = `
      ${message}
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert"></button>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 150);
      }
    }, 3000);
  }

  // Handle Add to Cart button clicks
  function handleAddToCart(event) {
    const button = event.target.closest('[data-add-to-cart]');
    if (!button) return;

    event.preventDefault();

    if (!Cart.isAvailable()) {
      showToast('<i class="bi bi-exclamation-triangle me-2"></i>Cart unavailable. Please enable localStorage.', 'danger');
      return;
    }

    let customizations = [];
    if (button.dataset.customizations) {
      try {
        const parsed = JSON.parse(button.dataset.customizations);
        if (Array.isArray(parsed)) customizations = parsed;
      } catch (e) {
        console.warn('Invalid data-customizations JSON, ignoring:', e);
      }
    }

    const item = {
      productId: button.dataset.productId,
      name: button.dataset.name,
      price: button.dataset.price,
      currency: button.dataset.currency || 'usd',
      imageUrl: button.dataset.imageUrl || '',
      esignTemplateId: button.dataset.esignTemplateId || '',
      maxQuantity: button.dataset.maxQuantity || '99',
      customizations: customizations
    };

    if (!item.productId || !item.name || !item.price) {
      console.error('Invalid product data:', item);
      showToast('<i class="bi bi-exclamation-triangle me-2"></i>Error adding item to cart.', 'danger');
      return;
    }

    try {
      Cart.addItem(item, 1);
      showToast(`<i class="bi bi-check-circle me-2"></i><strong>${item.name}</strong> added to cart!`, 'success');
      updateCartBadge();
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast(`<i class="bi bi-exclamation-triangle me-2"></i>${error.message}`, 'danger');
    }
  }

  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    document.body.addEventListener('click', handleAddToCart);
    window.addEventListener('cartUpdated', updateCartBadge);
    updateCartBadge();
  });
})();

export { Cart }
